/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";

import { Helmet } from "react-helmet";

import DataTable from "react-data-table-component";
import Input from "../../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { FiTrash } from "react-icons/fi";
import { IoClose, IoEyeSharp } from "react-icons/io5";

/* TOAST NOTIFICATION */
import { toast } from "react-hot-toast";

/* API IMPORT */
import {
  deleteRegistrationFormDetailsById,
  getRegistrationFormList,
} from "../../../service/api";

const RegistrationForm = () => {
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  const [registrationFormList, setRegistrationFormList] = useState([]);

  const [totalDocs, setTotalDocs] = useState([]);

  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const [viewModal, setViewModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
    getRegistrationFormListFunction();
  };

  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
    getRegistrationFormListFunction();
  };

  const getRegistrationFormListFunction = () => {
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };
    if (search) {
      params.search = search;
    }

    getRegistrationFormList(params)
      .then((res) => {
        setRegistrationFormList(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("e-->", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    deleteRegistrationFormDetailsById(deleteId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(!deleteModal);
        setDeleteId(null);
        getRegistrationFormListFunction();
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0,
      selector: (row, index) => (
        <span className="text-sm font-ibm-semibold text-black text-opacity-60">
          {index + 1}
        </span>
      ),
    },
    {
      name: "Name",
      grow: 1.5,
      selector: (row) => (
        <div className="p-0">
          <p className="text-base font-ibm-semibold text-black capitalize">
            {row.firstName} {row?.middleName} {row?.lastName}
          </p>
          <a
            href={`mailto:${row.email}`}
            className="block mt-1 text-sm font-ibm-regular text-greyText hover:text-logoYellow transition-all duration-300 ease-in-out"
          >
            {row.email}
          </a>
        </div>
      ),
    },
    {
      name: "Phone",
      grow: 1,
      selector: (row) => (
        <a
          href={`tel:${row.phoneNumber}`}
          className="text-sm font-ibm-regular text-black hover:text-logoYellow transition-all duration-300 ease-in-out"
        >
          {row.phoneNumber}
        </a>
      ),
    },
    {
      name: "Designation",
      grow: 1,
      selector: (row) => (
        <span className="text-sm font-ibm-regular text-greyText">
          {row?.designation === undefined ? "-" : row?.designation}
        </span>
      ),
    },
    {
      name: "Date & Time",
      grow: 1,
      selector: (row) => (
        <>
          <p className="text-sm font-ibm-regular text-greyText">
            {new Date(row.createdAt).toLocaleDateString("en-gb", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <p className="mt-1 text-sm font-ibm-regular text-greyText">
            {new Date(row.createdAt).toLocaleTimeString("en-gb", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })}
          </p>
        </>
      ),
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="flex items-center gap-2">
          <Tooltip content="View" className="text-sm font-ibm-semibold">
            <Button
              onClick={() => {
                setSelectedContact(row);
                setViewModal(true);
              }}
              className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent border border-authBorder hover:bg-black hover:border-black transition-all duration-300 ease-in-out text-greyText hover:text-white"
            >
              <IoEyeSharp className="w-4 h-4" />
            </Button>
          </Tooltip>
          <Tooltip content="Remove" className="text-sm font-ibm-semibold">
            <Button
              onClick={() => {
                setDeleteId(row?._id);
                setDeleteModal(!deleteModal);
              }}
              className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent border border-authBorder hover:bg-black hover:border-black transition-all duration-300 ease-in-out text-greyText hover:text-white"
            >
              <FiTrash className="w-4 h-4" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    getRegistrationFormListFunction();
  }, [page, sizePerPage, search]);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no queries to display.
        </p>
      </div>
    );
  };

  return (
    <section className="p-2 xl:p-5 lg:p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Registration Form Queries | LIBF</title>
      </Helmet>

      {/* Adjusted Width */}
      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Register your interest
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage the queries received from your website of the people who is
              interested to register for the event.
            </h2>
          </div>
          {/* SEARCHBAR */}
          <div className="w-full xl:w-[30vh] lg:w-[30vh] mt-2 xl:mt-0 lg:mt-0">
            <Input
              type="text"
              placeholder="Search name here..."
              name="search"
              id="search"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
            />
          </div>
        </div>

        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? registrationFormList : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>
      </div>

      {/* DELETE DETAILS MODAL */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              Delete details
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setDeleteModal(!deleteModal)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>
          <div className="p-5 border-b border-authBorder">
            <p className="text-base font-ibm-regular text-black">
              Are you sure you want to delete the details of this query?
            </p>
          </div>
          <div className="flex items-center justify-end px-5 py-3">
            <Button
              onClick={handleDelete}
              className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
            >
              Delete
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* VIEW DETAILS MODAL */}
      <Dialog
        open={viewModal}
        handler={() => setViewModal(!viewModal)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="rounded-xl p-1">
          {/* HEADER */}
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between bg-white">
            <h5 className="text-base font-ibm-semibold text-gray-800 tracking-tight">
              View Contact Details
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setViewModal(!viewModal)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <div className="py-5 px-3 border-b border-gray-200 bg-white">
            {selectedContact && (
              <div className="space-y-4">
                {/* NAME */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">Name</p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedContact.firstName} {selectedContact?.middleName}{" "}
                    {selectedContact.lastName}
                  </p>
                </div>

                {/* EMAIL */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Email
                  </p>
                  <a
                    href={`mailto:${selectedContact.email}`}
                    className="text-base font-ibm-semibold text-black hover:text-logoYellow"
                  >
                    {selectedContact.email}
                  </a>
                </div>

                {/* PHONE NUMBER */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Phone number
                  </p>
                  <a
                    href={`mailto:${selectedContact.phoneNumber}`}
                    className="text-base font-ibm-semibold text-black hover:text-logoYellow"
                  >
                    {selectedContact.phoneNumber}
                  </a>
                </div>

                {/* WHATSAPP PHONE NUMBER */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Phone number
                  </p>
                  <a
                    href={`mailto:${selectedContact.whatsAppPhoneNumber}`}
                    className="text-base font-ibm-semibold text-black hover:text-logoYellow"
                  >
                    {selectedContact.whatsAppPhoneNumber}
                  </a>
                </div>

                {/* DESIGNATION */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Designation
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedContact.designation}
                  </p>
                </div>

                {/* DESIGNATION */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">Age</p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedContact.age}
                  </p>
                </div>

                {/* ADDRESS */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Address
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedContact?.address?.address},{" "}
                    {selectedContact?.address?.addressLine1}
                    {selectedContact?.address?.city},{" "}
                    {selectedContact?.address?.state},{" "}
                    {selectedContact?.address?.zipCode}
                  </p>
                </div>

                {/* BELONG TO LOHANA */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Do they belong to Lohana community
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedContact?.belongToLohana ? "Yes" : "No"}
                  </p>
                </div>

                {/* SPONSOR */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Do they want to become a sponsor
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedContact?.isSponsor ? "Yes" : "No"}
                  </p>
                </div>

                {/* DATE AND TIME */}
                <div className="form-group">
                  <p className="text-sm font-ibm-regular text-greyText">
                    Date and Time
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {new Date(selectedContact?.createdAt).toLocaleDateString(
                      "en-gb",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      }
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default RegistrationForm;
